<template>
  <div class="container-fluid py-4">

    <!-- Header + actions -->
    <div class="row mb-4">
      <div class="col-12 d-flex justify-content-between align-items-center">
        <h5 class="text-dark mb-0 fw-bold">{{ $t('bonusesList.title') }}</h5>

        <div class="d-flex gap-2">
          <button class="btn btn-outline-secondary" @click="exportToExcel">
            <i class="fas fa-file-export me-1"></i> {{ $t('bonusesList.export') }}
          </button>

          <router-link to="/add-bonus">
            <button class="btn text-white" style="background-color:#4b5563">
              <i class="fas fa-plus-circle me-1"></i> {{ $t('bonusesList.addBonus') }}
            </button>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Card -->
    <div class="card shadow-sm border-0 mb-4">
      <div class="card-header bg-burgundy text-white py-3">
         <h6 class="mb-0 text-white fw-bold">{{ $t('bonusesList.tableTitle') }}</h6>
      </div>

      <!-- Search / Filter -->
      <div class="card-body border-bottom">
        <div class="row g-2 align-items-center">
          <div class="col-md-6">
            <div class="input-group">
              <span class="input-group-text bg-white border-end-0">
                <i class="fas fa-search text-muted"></i>
              </span>
              <input
                type="text"
                v-model="searchQuery"
                class="form-control border-start-0"
                :placeholder="$t('bonusesList.searchPlaceholder')"
              />
            </div>
          </div>

          <div class="col-md-6 d-flex justify-content-md-end">
            <select v-model="statusFilter" class="form-select" style="width:auto;">
              <option value="all">{{ $t('bonusesList.allStatus') }}</option>
              <option value="active">{{ $t('bonusesList.active') }}</option>
              <option value="inactive">{{ $t('bonusesList.inactive') }}</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover m-0">
            <thead class="bg-light">
              <tr>
                <th class="ps-4">{{ $t('bonusesList.colTitle') }}</th>
                <th>{{ $t('bonusesList.colDate') }}</th>
                <th>{{ $t('bonusesList.colDocument') }}</th>
                <th>{{ $t('bonusesList.colStatus') }}</th>
                <th class="text-center">{{ $t('bonusesList.colActions') }}</th>
              </tr>
            </thead>

            <tbody>

              <tr v-if="loading">
                <td colspan="5" class="text-center py-4">
                  <i class="fas fa-spinner fa-spin me-2"></i>
                  {{ $t('bonusesList.loading') }}
                </td>
              </tr>

              <tr v-else-if="filteredItems.length === 0">
                <td colspan="5" class="text-center py-5 text-muted">
                  <i class="fas fa-money-check-alt mb-2" style="font-size:1.6rem;"></i>
                  <div>{{ $t('bonusesList.noResults') }}</div>
                </td>
              </tr>

              <template v-else>
                <tr v-for="row in filteredItems" :key="row.id">
                  <td class="ps-4">{{ row.title }}</td>
                  <td><span class="text-muted">{{ row.date }}</span></td>

                  <td>
                    <a v-if="row.document_url" :href="row.document_url" target="_blank"
                       class="btn btn-outline-dark btn-sm">
                      <i class="fas fa-download me-1"></i> {{ $t('bonusesList.download') }}
                    </a>
                    <span v-else class="text-muted small">—</span>
                  </td>

                  <td>
                    <span class="badge rounded-pill" :class="row.active ? 'bg-success' : 'bg-secondary'">
                      {{ row.active ? $t('bonusesList.active') : $t('bonusesList.inactive') }}
                    </span>
                  </td>

                  <td>
                    <div class="d-flex justify-content-center gap-2">

                      <!-- Toggle -->
                      <button class="btn btn-ico btn-outline-secondary"
                              :title="row.active ? $t('bonusesList.deactivate') : $t('bonusesList.activate')"
                              @click="toggleStatus(row)">
                        <i class="fas" :class="row.active ? 'fa-toggle-on' : 'fa-toggle-off'"></i>
                      </button>

                      <!-- Edit -->
                      <router-link :to="`/edit-bonus/${row.id}`"
                                   class="btn btn-ico btn-outline-primary"
                                   :title="$t('bonusesList.edit')">
                        <i class="fas fa-pen"></i>
                      </router-link>

                      <!-- Delete -->
                      <button class="btn btn-ico btn-outline-danger"
                              :title="$t('bonusesList.delete')"
                              @click="deleteItem(row.id)">
                        <i class="fas fa-trash"></i>
                      </button>

                    </div>
                  </td>

                </tr>
              </template>

            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <div class="px-4 py-3 d-flex justify-content-end" v-if="pagination.total > 0">
        <button class="nav-circle me-2"
                :disabled="pagination.current_page === 1 || loading"
                @click="loadPage(pagination.current_page - 1)">
          <i class="fas fa-chevron-left"></i>
        </button>

        <button class="nav-circle"
                :disabled="pagination.current_page === pagination.last_page || loading"
                @click="loadPage(pagination.current_page + 1)">
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>

    </div>

  </div>
</template>
<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from '@/api'
import { useToast } from 'vue-toastification'
import * as XLSX from 'xlsx'

const toast = useToast()

const ENDPOINT = '/financial-requests/monthly-bonuses'
/* ✅ toggle attendu par tes routes :
   PATCH monthly-bonuses/{monthlyBonus}/toggle-status */
const TOGGLE = (id) => `/financial-requests/monthly-bonuses/${id}/toggle-status`

const loading = ref(true)
const items = ref([])
const searchQuery = ref('')
const statusFilter = ref('all')
const pagination = ref({ current_page: 1, last_page: 1, total: 0 })

async function fetchPage(page = 1) {
  try {
    loading.value = true
    const { data } = await axios.get(ENDPOINT, { params: { page } })
    const rows = Array.isArray(data?.data) ? data.data : []
    items.value = rows.map(x => ({
      id: x.id,
      title: x.title || '',
      date: x.date || '',
      active: !!x.active,
      document_url: x.document_url || null,
    }))
    pagination.value = {
      current_page: data.current_page ?? 1,
      last_page: data.last_page ?? 1,
      total: data.total ?? rows.length,
    }
  } catch (e) {
    console.error('Error fetching monthly bonuses:', e)
    toast.error('Failed to load monthly bonuses')
  } finally {
    loading.value = false
  }
}

onMounted(() => fetchPage())

const filteredItems = computed(() => {
  const q = (searchQuery.value || '').toLowerCase()
  return items.value.filter(r => {
    const base = `${r.title} ${r.date}`.toLowerCase()
    const statusOK =
      statusFilter.value === 'all' ||
      (statusFilter.value === 'active' && r.active) ||
      (statusFilter.value === 'inactive' && !r.active)
    return statusOK && base.includes(q)
  })
})

function loadPage(n) {
  if (n < 1 || n > pagination.value.last_page) return
  fetchPage(n)
}

async function toggleStatus(row) {
  const prev = row.active
  row.active = !row.active
  try {
    const { data } = await axios.patch(TOGGLE(row.id))
    const apiActive = data?.data?.active
    if (typeof apiActive === 'boolean') row.active = apiActive
    toast.success('Status updated')
  } catch (e) {
    row.active = prev
    console.error('Error toggling status:', e)
    toast.error('Failed to update status')
  }
}

async function deleteItem(id) {
  if (!confirm('Are you sure you want to delete this monthly bonus?')) return
  try {
    await axios.delete(`${ENDPOINT}/${id}`)
    items.value = items.value.filter(x => x.id !== id)
    toast.success('Monthly bonus deleted')
    if (items.value.length === 0 && pagination.value.current_page > 1) {
      loadPage(pagination.value.current_page - 1)
    }
  } catch (e) {
    console.error('Error deleting monthly bonus:', e)
    toast.error('Failed to delete monthly bonus')
  }
}

function exportToExcel() {
  const rows = filteredItems.value.map(r => ({
    Title: r.title,
    Date: r.date,
    Status: r.active ? 'Active' : 'Inactive',
    Document: r.document_url ? 'Yes' : 'No'
  }))
  const ws = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'MonthlyBonuses')
  XLSX.writeFile(wb, 'MonthlyBonuses_Export.xlsx')
}
</script>

<style scoped>
.bg-burgundy{background: linear-gradient(195deg, #4b5563, #8d2e38);}
.table th{font-size:.875rem;text-transform:uppercase;letter-spacing:.025em;}
.table td{padding-top:.75rem;padding-bottom:.75rem;vertical-align:middle;}
.input-group-text,.form-control{padding:.6rem 1rem;border-color:#e9ecef;}
.form-select{padding:.6rem 2rem .6rem 1rem;border-color:#e9ecef;background:#fff;}
.btn{padding:.5rem 1rem;font-weight:600;border-radius:.25rem;text-transform:uppercase;font-size:.875rem;}
.btn-ico{width:40px;height:40px;padding:0;display:inline-flex;align-items:center;justify-content:center;border-radius:.5rem;font-size:1.05rem;}
.nav-circle{width:42px;height:42px;border-radius:999px;border:1px solid #e9ecef;background:#fff;color:#b03a45;display:inline-flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,.06);transition:.15s;}
.nav-circle:hover{background:#b03a45;color:#fff;border-color:#b03a45;}
.nav-circle:disabled{opacity:.45;cursor:not-allowed;background:#fff;color:#b03a45;border-color:#e9ecef;box-shadow:none;}
.container-fluid{max-width:1320px;}
.card{border-radius:.5rem;overflow:hidden;}
.card-header{border-bottom:none;}
</style>
